/**
 * DSScreenUtils - Fullscreen Management Utilities
 *
 * This module provides robust fullscreen functionality with proper error handling
 * to prevent "Permissions check failed" errors that occur when:
 *
 * 1. Fullscreen is requested without user interaction (click, touch, keypress)
 * 2. Browser security policies block automatic fullscreen requests
 * 3. Fullscreen API is not supported by the browser
 *
 * Key Functions:
 * - enterFullScreenSafely(): Attempts fullscreen only if user has interacted
 * - exitFullScreenSafely(): Safely exits fullscreen with error handling
 * - hasUserInteracted(): Checks if user has performed any interaction
 * - isFullscreenSupported(): Checks browser support for fullscreen API
 * - requestFullScreenWithUserPrompt(): Waits for user interaction before fullscreen
 *
 * Usage:
 * - Use the "Safely" functions for automatic fullscreen attempts
 * - Use the regular functions only when you're certain user interaction occurred
 * - Always handle the boolean return values to sync UI state
 */

// Track if user has interacted with the page
let userHasInteracted = false;

// Set up user interaction listeners
if (typeof window !== "undefined") {
  const markUserInteraction = () => {
    userHasInteracted = true;
  };

  // Listen for various user interaction events
  ["click", "touchstart", "keydown", "mousedown"].forEach((eventType) => {
    document.addEventListener(eventType, markUserInteraction, { once: true, passive: true });
  });
}

export function hasUserInteracted(): boolean {
  return userHasInteracted;
}

export function isFullscreenSupported(): boolean {
  const element = document.documentElement;
  return !!(
    element.requestFullscreen ||
    (element as any).mozRequestFullScreen ||
    (element as any).webkitRequestFullscreen ||
    (element as any).msRequestFullscreen
  );
}

export function isCurrentlyFullscreen(): boolean {
  return !!(
    document.fullscreenElement ||
    (document as any).mozFullScreenElement ||
    (document as any).webkitFullscreenElement ||
    (document as any).msFullscreenElement
  );
}

export async function enterFullScreenSafely(): Promise<boolean> {
  if (!isFullscreenSupported()) {
    console.warn("[DS_FS] Fullscreen API not supported");
    return false;
  }

  if (!hasUserInteracted()) {
    console.warn("[DS_FS] Cannot enter fullscreen without user interaction");
    return false;
  }

  try {
    await enterFullScreen();
    return true;
  } catch (error) {
    console.warn("[DS_FS] Failed to enter fullscreen safely:", error);
    return false;
  }
}

export async function exitFullScreenSafely(): Promise<boolean> {
  if (!isCurrentlyFullscreen()) {
    console.log("[DS_FS] Not currently in fullscreen mode");
    return true;
  }

  try {
    await exitFullScreen();
    return true;
  } catch (error) {
    console.warn("[DS_FS] Failed to exit fullscreen safely:", error);
    return false;
  }
}

export async function enterFullScreen(): Promise<void> {
  const element = document.documentElement;

  try {
    if (element.requestFullscreen) {
      await element.requestFullscreen();
    } else if ((element as any).mozRequestFullScreen) {
      // Firefox
      await (element as any).mozRequestFullScreen();
    } else if ((element as any).webkitRequestFullscreen) {
      // Chrome, Safari, Opera
      await (element as any).webkitRequestFullscreen();
    } else if ((element as any).msRequestFullscreen) {
      // IE/Edge
      await (element as any).msRequestFullscreen();
    } else {
      throw new Error("[DS_FS] Fullscreen API not supported");
    }
  } catch (error: any) {
    // Handle specific permission errors more gracefully
    if (
      error?.message?.includes("permissions check failed") ||
      error?.name === "NotAllowedError" ||
      error?.code === 0
    ) {
      console.warn("[DS_FS] Fullscreen request denied - user interaction may be required:", error.message);
      throw new Error("[DS_FS] Fullscreen requires user interaction (click, touch, or keypress)");
    } else if (error?.name === "TypeError" && error?.message?.includes("permissions check failed")) {
      console.warn("[DS_FS] Fullscreen permissions check failed:", error.message);
      throw new Error("[DS_FS] Fullscreen permission denied by browser");
    } else {
      console.error("[DS_FS] Failed to enter fullscreen:", error);
      throw error;
    }
  }
}

export async function exitFullScreen(): Promise<void> {
  try {
    if (document.exitFullscreen) {
      await document.exitFullscreen();
    } else if ((document as any).mozCancelFullScreen) {
      // Firefox
      await (document as any).mozCancelFullScreen();
    } else if ((document as any).webkitExitFullscreen) {
      // Chrome, Safari, Opera
      await (document as any).webkitExitFullscreen();
    } else if ((document as any).msExitFullscreen) {
      // IE/Edge
      await (document as any).msExitFullscreen();
    } else {
      throw new Error("[DS_FS] Exit fullscreen API not supported");
    }
  } catch (error: any) {
    // Handle specific permission errors more gracefully
    if (
      error?.message?.includes("permissions check failed") ||
      error?.name === "NotAllowedError" ||
      error?.code === 0
    ) {
      console.warn("[DS_FS] Exit fullscreen request denied:", error.message);
      throw new Error("[DS_FS] Exit fullscreen permission denied by browser");
    } else {
      console.error("[DS_FS] Failed to exit fullscreen:", error);
      throw error;
    }
  }
}

export async function requestFullScreenWithUserPrompt(): Promise<boolean> {
  if (!isFullscreenSupported()) {
    console.warn("[DS_FS] Fullscreen API not supported");
    return false;
  }

  if (hasUserInteracted()) {
    return await enterFullScreenSafely();
  }

  // If user hasn't interacted, we can't automatically enter fullscreen
  // This would typically be handled by showing a UI prompt to the user
  console.log("[DS_FS] Fullscreen requires user interaction - waiting for user to interact");

  return new Promise((resolve) => {
    const handleInteraction = async () => {
      // Remove listeners
      ["click", "touchstart", "keydown", "mousedown"].forEach((eventType) => {
        document.removeEventListener(eventType, handleInteraction);
      });

      // Try to enter fullscreen now that user has interacted
      const success = await enterFullScreenSafely();
      resolve(success);
    };

    // Add listeners for user interaction
    ["click", "touchstart", "keydown", "mousedown"].forEach((eventType) => {
      document.addEventListener(eventType, handleInteraction, { once: true, passive: true });
    });

    // Timeout after 30 seconds
    setTimeout(() => {
      ["click", "touchstart", "keydown", "mousedown"].forEach((eventType) => {
        document.removeEventListener(eventType, handleInteraction);
      });
      resolve(false);
    }, 30000);
  });
}
